import React from "react";
import {
  Box,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import "./EditProductDialog.Module.css";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DateRangePicker } from "mui-daterange-picker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import {
  getDates,
  checkIfDateExists,
  ReturnDaysFromTimeline,
  filterMultiples,
  AIRPORT_TIMESLOT_OPTIONS,
  isAirportMediaListing,
  getMaxTimeslotOptionsAfterMin,
} from "../marketplaceUtils";
import {
  FieldBoxLabel,
  FieldBoxInput,
  CommonTextStyle,
} from "../marketplaceStyles";

const CloseIconImg = 'https://bxidevelopment1.s3.ap-south-1.amazonaws.com/assets/CloseIcon.svg';


const SECONDS_FIELD_ARR = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95,
  100, 105, 110, 115, 120, 125, 130, 135, 140, 145, 150, 155, 160, 165, 170,
  175, 180,
];

const options = { day: "2-digit", month: "short", year: "numeric" };

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "dayIsBetween" && prop !== "isFirstDay" && prop !== "isLastDay",
})(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
  }),
  ...(isLastDay && {
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  }),
}));

function Day(props) {
  const { day, selectedDay, TimelineData, ...other } = props;
  if (selectedDay == null) {
    return <PickersDay day={day} {...other} />;
  }
  const start = selectedDay;
  const end = start.add(ReturnDaysFromTimeline(TimelineData), "day");
  const dayIsBetween = day.isBetween(start, end, null, "[]");
  const isFirstDay = day.isSame(start, "day");
  const isLastDay = day.isSame(end, "day");
  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={dayIsBetween ? { px: 2.5, mx: 0 } : {}}
      dayIsBetween={dayIsBetween}
      isFirstDay={isFirstDay}
      isLastDay={isLastDay}
    />
  );
}

export default function EditProductDialog({
  open,
  onClose,
  disableEnforceFocus = false,
  formData,
  onInputChange,
  onVariationChange,
  onKeyDown,
  onSave,
  dateArr,
  setDateArr,
  calendarOpen,
  setCalendarOpen,
  calendarValue,
  setCalendarValue,
  maxtimeslotArr,
  setMaxtimeslotArr,
  inputRef,
  isPrintMedia = false,
  isMultiplexMedia = false,
  multiplexExcelRowCount = null,
  isDoohMedia = false,
  doohExcelRowCount = null,
  isHoardingMedia = false,
  hoardingExcelRowCount = null,
  isDigitalVoucher = false,
}) {
  const HOARDING_TIMELINE_OPTIONS = [10, 20, 30];
  const handleToggle = () => setCalendarOpen((prev) => !prev);
  const isAirport = isAirportMediaListing(formData);
  const timeslotMinOptions = isAirport ? AIRPORT_TIMESLOT_OPTIONS : SECONDS_FIELD_ARR;
  const readOnlyQuantityStyle = {
    ...FieldBoxInput,
    backgroundColor: "#f8fafc",
    color: "#475569",
  };

  return (
    <Dialog
      open={open}
      className="edit-product-dialog custom-popup"
      onClose={onClose}
      disableEnforceFocus={disableEnforceFocus}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 680,
          maxHeight: "90vh",
          borderRadius: 3,
          color: "#121317",
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle component="div" className="edit-product-dialog__header" sx={{ position: "relative", pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "primary.main", fontSize: 26 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#121317", fontSize: 20 }}>
            Edit product details
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "#64748b" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="edit-product-dialog__body" dividers={false}>
        <Box className="edit-product-dialog__scroll">
          {/* Status & approval — admin actions first */}
          <Box className="edit-product-dialog__section">
            <Typography className="edit-product-dialog__section-title">Status & approval</Typography>
            <Box className="edit-product-dialog__status-card">
              <Box className="edit-product-dialog__field-row">
                <FormControl fullWidth size="small">
                  <InputLabel>Listing status</InputLabel>
                  <Select
                    name="status"
                    value={formData?.status || ""}
                    onChange={onInputChange}
                    label="Listing status"
                  >
                    <MenuItem value="">Select option</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="pendingapproval">Pending approval</MenuItem>
                    <MenuItem value="Review">In Review</MenuItem>
                    <MenuItem value="Delist">Delisted</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {formData?.status === "Rejected" && (
                <TextField
                  fullWidth
                  size="small"
                  name="rejectionReason"
                  value={formData?.rejectionReason || ""}
                  onChange={onInputChange}
                  placeholder="Reason for rejection (required)"
                  required
                  sx={{ mt: 2 }}
                />
              )}
              {formData?.status === "Review" && (
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    name="reviewReason"
                    value={formData?.reviewReason || ""}
                    onChange={onInputChange}
                    placeholder="Reason for review"
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Navigation status</InputLabel>
                    <Select
                      name="reviewReasonNavigation"
                      value={formData?.reviewReasonNavigation || ""}
                      onChange={onInputChange}
                      label="Navigation status"
                    >
                      <MenuItem value="">Select option</MenuItem>
                      <MenuItem value="ProductInformation">Product information</MenuItem>
                      <MenuItem value="TechnicalInformation">Technical information</MenuItem>
                      <MenuItem value="GoLive">Go live</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Basic information */}
          <Box className="edit-product-dialog__section">
            <Typography className="edit-product-dialog__section-title">Basic information</Typography>
            <Box className="edit-product-dialog__field-row">
              <TextField
                fullWidth
                size="small"
                name="productName"
                value={formData?.productName || ""}
                onChange={onInputChange}
                placeholder="Product / service name"
                inputRef={inputRef}
                label="Product name"
              />
              <TextField
                fullWidth
                size="small"
                name="productType"
                value={formData?.productType || ""}
                label="Listing type"
                disabled
              />
            </Box>
          </Box>

          {/* Variants & pricing */}
          <Box className="edit-product-dialog__section">
            <Typography className="edit-product-dialog__section-title">Variants & pricing</Typography>
          {formData?.ProductsVariantions?.map((el, idx) => (
            <Box key={el?._id ?? idx} className="edit-product-dialog__variant-block">
              <Box className="edit-product-dialog__variant-card">
                <Box
                  className="edit-product-dialog__variant-header"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}
                >
                  {formData?.productType !== "Media" && el?.ProductColor ? (
                    <Box
                      className="edit-product-dialog__color-swatch"
                      sx={{ backgroundColor: el.ProductColor }}
                      title="Product color"
                    />
                  ) : null}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Variant {idx + 1}
                  </Typography>
                </Box>

                <Stack
                  className="edit-product-dialog__variant-text-fields"
                  spacing={2.5}
                  sx={{ width: "100%", mb: 1 }}
                >
                  {!isMultiplexMedia && !isDoohMedia && !isHoardingMedia ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Variant name"
                      value={el?.VariantName != null ? String(el.VariantName) : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.length <= 120) {
                          onVariationChange(idx, "VariantName", v);
                        }
                      }}
                      placeholder="e.g. size, pack, or SKU label"
                      inputProps={{ maxLength: 120 }}
                    />
                  ) : null}

                  <TextField
                    fullWidth
                    size="small"
                    label="HSN code"
                    value={el?.HSN != null && el?.HSN !== "" ? String(el.HSN) : ""}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 8);
                      onVariationChange(idx, "HSN", digitsOnly);
                    }}
                    placeholder="4, 6, or 8 digits"
                    inputProps={{ inputMode: "numeric", maxLength: 8 }}
                    FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                  />
                </Stack>

              <Box sx={{ display: "flex", flexDirection: "row", width: "100%", flexWrap: "wrap", gap: 2 }}>
                {formData?.productType !== "Voucher" && (
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <label style={FieldBoxLabel}>Discounted Price</label>
                    <br />
                    <input
                      type="number"
                      name="price"
                      value={el?.DiscountedPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          onVariationChange(idx, "DiscountedPrice", value);
                        }
                      }}
                      onKeyDown={onKeyDown}
                      style={FieldBoxInput}
                      min="0"
                    />
                  </Box>
                )}
                {formData?.productType !== "Media" && (
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <label style={FieldBoxLabel}>Price Per Unit</label>
                    <br />
                    <input
                      type="number"
                      name="PricePerUnit"
                      value={el?.PricePerUnit}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (formData?.productType === "Voucher" && /^\d*\.?\d*$/.test(value)) {
                          onVariationChange(idx, "DiscountedPrice", value);
                        }
                        if (/^\d*\.?\d*$/.test(value)) {
                          onVariationChange(idx, "PricePerUnit", value);
                        }
                      }}
                      onKeyDown={onKeyDown}
                      style={FieldBoxInput}
                      min="0"
                    />
                  </Box>
                )}
                {formData?.productType === "Media" && (
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <label style={FieldBoxLabel}>Price Per Unit</label>
                    <br />
                    <input
                      type="number"
                      name="PricePerUnit"
                      value={el?.PricePerUnit}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          onVariationChange(idx, "PricePerUnit", value);
                        }
                      }}
                      style={FieldBoxInput}
                      onKeyDown={onKeyDown}
                      min="0"
                    />
                  </Box>
                )}
                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  <label style={FieldBoxLabel}>HSN</label>
                  <br />
                  <input
                    type="text"
                    inputMode="numeric"
                    name="HSN"
                    value={el?.HSN != null && el?.HSN !== "" ? String(el.HSN) : ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 8) {
                        onVariationChange(idx, "HSN", value);
                      }
                    }}
                    style={FieldBoxInput}
                    placeholder="4, 6 or 8 digits"
                    maxLength={8}
                  />
                </Box>
              </Box>

              {(formData?.productType !== "Media" ||
                formData?.ProductSubCategoryName !== "News Papers / Magazines") && (
                  <Box sx={{ display: "flex", flexDirection: "row", width: "100%", gap: 5 }}>
                    {isMultiplexMedia || isDoohMedia || isHoardingMedia ? (
                      <Box sx={{ width: "50%" }}>
                        <label style={FieldBoxLabel}>Units</label>
                        <br />
                        <input
                          type="text"
                          value={
                            (isMultiplexMedia
                              ? multiplexExcelRowCount
                              : isDoohMedia
                                ? doohExcelRowCount
                                : hoardingExcelRowCount) ?? ""
                          }
                          readOnly
                          disabled
                          style={{
                            ...FieldBoxInput,
                            backgroundColor: "#f8fafc",
                            color: "#475569",
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ width: "50%" }}>
                          <label style={FieldBoxLabel}>
                            Min Quantity {formData?.productType === "Media" ? "/ Unit" : ""}
                          </label>
                          <br />
                          <input
                            type="number"
                            name="MinQuantity"
                            value={el?.MinOrderQuantity}
                            onChange={(e) => {
                              if (isDigitalVoucher) return;
                              const value = e.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                onVariationChange(idx, "MinOrderQuantity", value);
                              }
                              if (formData?.productType === "Media" && /^\d*\.?\d*$/.test(value)) {
                                onVariationChange(idx, "minOrderQuantityunit", value);
                              }
                            }}
                            style={isDigitalVoucher ? readOnlyQuantityStyle : FieldBoxInput}
                            readOnly={isDigitalVoucher}
                            disabled={isDigitalVoucher}
                            onKeyDown={isDigitalVoucher ? undefined : onKeyDown}
                            min="0"
                          />
                        </Box>
                        <Box sx={{ width: "50%" }}>
                          <label style={FieldBoxLabel}>
                            Max Quantity {formData?.productType === "Media" ? "/ Unit" : ""}
                          </label>
                          <br />
                          <input
                            type="number"
                            name="MaxQuantity"
                            value={el?.MaxOrderQuantity}
                            onChange={(e) => {
                              if (isDigitalVoucher) return;
                              const value = e.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                onVariationChange(idx, "MaxOrderQuantity", value);
                              }
                              if (formData?.productType === "Media" && /^\d*\.?\d*$/.test(value)) {
                                onVariationChange(idx, "maxOrderQuantityunit", value);
                              }
                            }}
                            style={isDigitalVoucher ? readOnlyQuantityStyle : FieldBoxInput}
                            readOnly={isDigitalVoucher}
                            disabled={isDigitalVoucher}
                            onKeyDown={isDigitalVoucher ? undefined : onKeyDown}
                            min="0"
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                )}

              {formData?.productType === "Media" &&
                !isHoardingMedia &&
                Number(el?.minTimeslotSeconds) > 0 && (
                <Box sx={{ display: "flex", flexDirection: "row", width: "100%", gap: 5 }}>
                  <Box sx={{ width: "50%" }}>
                    <label style={FieldBoxLabel}>Min Timeslot Seconds</label>
                    <br />
                    <select
                      style={{
                        width: "92%",
                        height: "40px",
                        border: "1px solid #e8e8e8",
                        borderRadius: 2,
                        padding: "0 10px",
                        WebkitAppearance: "none",
                      }}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        onVariationChange(idx, "minTimeslotSeconds", selectedValue);
                        const minNum = Number(selectedValue);
                        setMaxtimeslotArr(
                          isAirport
                            ? getMaxTimeslotOptionsAfterMin(minNum, AIRPORT_TIMESLOT_OPTIONS)
                            : filterMultiples(SECONDS_FIELD_ARR, minNum),
                        );
                      }}
                      defaultValue={el?.minTimeslotSeconds}
                    >
                      {timeslotMinOptions?.map((item, i) => (
                        <option value={item} key={i}>{item}</option>
                      ))}
                    </select>
                  </Box>
                  <Box sx={{ width: "50%" }}>
                    <label style={FieldBoxLabel}>Max Timeslot Seconds</label>
                    <br />
                    <select
                      style={{
                        width: "92%",
                        height: "40px",
                        border: "1px solid #e8e8e8",
                        borderRadius: 2,
                        padding: "0 10px",
                        WebkitAppearance: "none",
                      }}
                      onChange={(e) =>
                        onVariationChange(idx, "maxTimeslotSeconds", e.target.value)
                      }
                      defaultValue={el?.maxTimeslotSeconds}
                    >
                      {(isAirport
                        ? getMaxTimeslotOptionsAfterMin(
                            el?.minTimeslotSeconds,
                            AIRPORT_TIMESLOT_OPTIONS,
                          )
                        : maxtimeslotArr
                      )?.length <= 0 ? (
                        <option value={el?.maxTimeslotSeconds}>{el?.maxTimeslotSeconds}</option>
                      ) : (
                        (isAirport
                          ? getMaxTimeslotOptionsAfterMin(
                              el?.minTimeslotSeconds,
                              AIRPORT_TIMESLOT_OPTIONS,
                            )
                          : maxtimeslotArr
                        )?.map((item, i) => {
                          if (isAirport) {
                            return (
                              <option value={item} key={i}>
                                {item}
                              </option>
                            );
                          }
                          const minTimeslotSeconds = Number(el?.minTimeslotSeconds);
                          if (minTimeslotSeconds >= Number(item)) return null;
                          return <option value={item} key={i}>{item}</option>;
                        })
                      )}
                    </select>
                  </Box>
                </Box>
              )}

              {formData?.productType === "Media" && !isPrintMedia && (
                isHoardingMedia ? (
                  <Box sx={{ display: "flex", flexDirection: "row", width: "100%", gap: 5 }}>
                    <Box sx={{ width: "50%" }}>
                      <label style={FieldBoxLabel}>Timeline</label>
                      <br />
                      <select
                        style={{
                          width: "92%",
                          height: "40px",
                          border: "1px solid #e8e8e8",
                          borderRadius: 2,
                          padding: "0 10px",
                          WebkitAppearance: "none",
                        }}
                        value={
                          String(
                            el?.timeline ??
                              formData?.mediaVariation?.timeline ??
                              el?.minOrderQuantitytimeline ??
                              el?.maxOrderQuantitytimeline ??
                              "",
                          ).replace(/\s*days?$/i, "")
                        }
                        onChange={(e) => onVariationChange(idx, "timeline", e.target.value)}
                      >
                        <option value="">Select timeline</option>
                        {HOARDING_TIMELINE_OPTIONS.map((days) => (
                          <option key={days} value={String(days)}>
                            {days} days
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Box>
                ) : (
                <Box sx={{ display: "flex", flexDirection: "row", width: "100%", gap: 5 }}>
                  <Box sx={{ width: "50%" }}>
                    <label style={FieldBoxLabel}>
                      Min Order QTY Timeline ({isMultiplexMedia ? "Weeks" : el?.Timeline})
                    </label>
                    <br />
                    <input
                      type="number"
                      name="MinQuantity"
                      value={el?.minOrderQuantitytimeline}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          onVariationChange(idx, "minOrderQuantitytimeline", value);
                        }
                      }}
                      style={FieldBoxInput}
                      onKeyDown={onKeyDown}
                      min="0"
                    />
                  </Box>
                  <Box sx={{ width: "50%" }}>
                    <label style={FieldBoxLabel}>
                      Max Order QTY Timeline ({isMultiplexMedia ? "Weeks" : el?.Timeline})
                    </label>
                    <br />
                    <input
                      type="number"
                      name="MaxQuantity"
                      value={el?.maxOrderQuantitytimeline}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          onVariationChange(idx, "maxOrderQuantitytimeline", value);
                        }
                      }}
                      style={FieldBoxInput}
                      onKeyDown={onKeyDown}
                      min="0"
                    />
                  </Box>
                </Box>
                )
              )}
              </Box>
            </Box>
          ))}
          </Box>

          {/* Other details */}
          <Box className="edit-product-dialog__section">
            <Typography className="edit-product-dialog__section-title">Other details</Typography>
            <Box className="edit-product-dialog__field-row">
              <TextField
                fullWidth
                size="small"
                name="location"
                value={formData?.location || ""}
                onChange={onInputChange}
                placeholder="e.g. Mumbai"
                label="Location"
              />
              {formData?.productType !== "Media" && (
              <TextField
                fullWidth
                size="small"
                name="listperiod"
                value={formData?.listperiod || ""}
                onChange={onInputChange}
                placeholder="Listing days"
                label="Listing days"
              />
              )}
            </Box>
          </Box>
        </Box>
        <Box className="edit-product-dialog__footer" component="div">
          <Button variant="outlined" onClick={onClose} color="inherit" sx={{ minWidth: 100 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} color="primary" sx={{ minWidth: 120 }}>
            Save changes
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
